import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { KeyboardEvent } from 'react';
import { useCallback } from 'react';
import { useController, type FieldValues } from 'react-hook-form';
import { Form, Input, type InputProps } from 'semantic-ui-react';

import { useFormEnterSubmit } from '../form-enter-context';

import { CharacterCounter } from './character-counter';
import { FieldError } from './field-error';
import type { FieldProps, WithLimit } from './field.types';
import s from './field-input.scss';

export type FieldInputProps<T extends FieldValues = FieldValues> = FieldProps<
  T,
  InputProps,
  'label' | 'value'
> &
  WithLimit & {
    /**
     * Label content rendered for the field.
     *
     * - Without `labelPosition`: renders a standard `<label>` above the input.
     * - With `labelPosition`: renders as an SUI attached (pill-style) label
     *   inline with the input. A `<label htmlFor>` element is passed to SUI
     *   so clicking the pill focuses the input and screen readers announce it.
     */
    label?: InputProps['label'];
    /**
     * Activates the SUI attached-label layout and controls placement.
     * When set, `label` renders inline with the input as a pill instead
     * of above the field.
     */
    labelPosition?: InputProps['labelPosition'];
  };

/**
 * Text input field wired to React Hook Form via `useController`.
 *
 * Wraps SUI `Input` inside a `Form.Field` and handles the SUI `onChange`
 * unwrapping, error display, and `aria-errormessage` wiring automatically.
 *
 * All additional props are forwarded to the underlying SUI `Input`.
 */
export const FieldInput = <T extends FieldValues>({
  className,
  control,
  label,
  labelPosition,
  limit,
  name,
  required,
  width,
  ...rest
}: FieldInputProps<T>) => {
  useStyles(s);

  const onEnterSubmit = useFormEnterSubmit();
  const { field, fieldState } = useController({ control, name });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.nativeEvent.isComposing && onEnterSubmit) {
        e.preventDefault();
        onEnterSubmit();
      }
    },
    [onEnterSubmit],
  );
  const id = rest.id ?? name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;
  const hasCounter = typeof limit === 'number' && limit >= 0;

  // When labelPosition is set, pass a Label config object to SUI Input that
  // renders as a <label> element instead of SUI's default <div>. This keeps
  // the `ui label` pill styling while giving click-to-focus and a11y for free.
  const attachedLabel = labelPosition
    ? { as: 'label' as const, content: label, htmlFor: id }
    : undefined;

  return (
    <Form.Field
      className={className}
      error={hasError}
      required={required}
      width={width}
    >
      {label && !labelPosition && <label htmlFor={id}>{label}</label>}

      <div className={classNames(s.wrapper, hasCounter && s.hasCounter)}>
        <Input
          {...rest}
          ref={field.ref}
          aria-errormessage={hasError ? errorId : undefined}
          aria-invalid={hasError || undefined}
          id={id}
          label={attachedLabel}
          labelPosition={labelPosition}
          name={field.name}
          onBlur={field.onBlur}
          onChange={(_, { value }) => field.onChange(value)}
          onKeyDown={onEnterSubmit ? handleKeyDown : undefined}
          value={field.value ?? ''}
        />

        {hasCounter && (
          <CharacterCounter control={control} limit={limit} name={name} />
        )}
      </div>

      <FieldError id={errorId} message={fieldState.error?.message} />
    </Form.Field>
  );
};
